import { ChevronDownIcon } from "@chakra-ui/icons";
import { Box, Collapse, Flex, IconButton, Text, useDisclosure } from "@chakra-ui/react";
import { PropsWithChildren, ReactNode, createContext, useContext, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { AnimateSize } from "@nens/chakra/components";
import { RanaAlert, RanaHeading, TrashIcon } from "@nens/ranalib";

export const PROGRESS_DIALOG_STACK_ID = "progress-dialog-stack";

const ProgressDialogStackContext = createContext<HTMLDivElement | null | undefined>(undefined);

/**
 * Provides a shared portal anchor for all ProgressDialog instances.
 */
export function ProgressDialogStackProvider({ children }: PropsWithChildren) {
  const [stackEl, setStackEl] = useState<HTMLDivElement | null>(null);

  return (
    <ProgressDialogStackContext.Provider value={stackEl}>
      {children}
      <ProgressDialogStack stackRef={setStackEl} />
    </ProgressDialogStackContext.Provider>
  );
}

/**
 * Single fixed anchor for all ProgressDialog instances.
 * Render this once at the app level; every ProgressDialog portals into it
 * so multiple concurrent dialogs stack vertically instead of overlapping.
 */
interface ProgressDialogStackProps {
  stackRef?: (element: HTMLDivElement | null) => void;
}

export function ProgressDialogStack({ stackRef }: ProgressDialogStackProps) {
  return (
    <Box
      ref={stackRef}
      id={PROGRESS_DIALOG_STACK_ID}
      position="fixed"
      bottom={3}
      right={3}
      zIndex="max"
      display="flex"
      flexDirection="column"
      gap={3}
      alignItems="flex-end"
    />
  );
}

export interface ProgressDialogItem {
  id: string | number;
  label: string;
  sublabel?: string;
  status: "inProgress" | "success" | "error" | "warning";
  progress?: number;
  onCancel?: () => void;
  onClick?: () => void;
}

interface ProgressDialogProps {
  title: ReactNode;
  items: ProgressDialogItem[];
  defaultIsOpen?: boolean;
}

export function ProgressDialog({ title, items, defaultIsOpen = false }: ProgressDialogProps) {
  const { isOpen: isExpanded, onToggle } = useDisclosure({ defaultIsOpen });
  const stackEl = useContext(ProgressDialogStackContext);
  const hasWarnedAboutMissingProvider = useRef(false);

  const content = (
    <Collapse in={items.length > 0} unmountOnExit>
      <AnimateSize animateWidth>
        <Box w="fit" h="fit" minW="xs" bg="brand.bg">
          <Flex
            onClick={onToggle}
            px={4}
            py={3}
            justifyContent="space-between"
            alignItems="center"
            gap={4}
            cursor="pointer"
          >
            <RanaHeading type="h6" flexShrink={0}>
              {title}
            </RanaHeading>

            <Flex gap={2} alignItems="center" ml="auto">
              <ChevronDownIcon
                transform={isExpanded ? "rotate(0deg)" : "rotate(180deg)"}
                transition="transform 0.2s"
              />
            </Flex>
          </Flex>

          <Collapse in={isExpanded} animateOpacity>
            <Box maxH="xs" overflow="auto" pb={4}>
              {items.map((item) => (
                <RanaAlert
                  {...(item.onClick ? { onClick: item.onClick, cursor: "pointer" } : {})}
                  key={item.id}
                  status={item.status}
                  progress={item.progress}
                  _notLast={{ mb: 2 }}
                >
                  <Box overflow="hidden" whiteSpace="nowrap" textOverflow="ellipsis">
                    <Text color="brand.deep_sea" as="strong" title={item.label}>
                      {item.label}
                    </Text>
                    {item.sublabel && (
                      <Text fontWeight="light" whiteSpace="pre-wrap">
                        {item.sublabel}
                      </Text>
                    )}
                  </Box>

                  {item.onCancel && (
                    <IconButton
                      ml="auto"
                      flexShrink={0}
                      title="cancel"
                      aria-label="cancel"
                      variant="ghost"
                      size="sm"
                      icon={<TrashIcon boxSize={4} color="brand.deep_sea" />}
                      onClick={item.onCancel}
                    />
                  )}
                </RanaAlert>
              ))}
            </Box>
          </Collapse>
        </Box>
      </AnimateSize>
    </Collapse>
  );

  if (stackEl) {
    return createPortal(content, stackEl);
  }

  if (stackEl === undefined && !hasWarnedAboutMissingProvider.current) {
    hasWarnedAboutMissingProvider.current = true;
    console.warn(
      "ProgressDialogStackProvider not found. Please make sure to wrap the app with <ProgressDialogStackProvider>."
    );
  }

  return (
    <Box position="fixed" bottom={3} right={3}>
      {content}
    </Box>
  );
}
